/**
 * Settings → Billing & payouts — the tenant's ticket-revenue payouts.
 *
 * Fills the previously-stubbed `settings.billing` route. Read-only summary of
 * balance / next payout / platform fee + a payout history, with a deep-link
 * into the connected provider's dashboard (Connect Standard: the provider owns
 * the payout ledger + bank details). No baked data — everything is read from
 * `getPayoutsOverview`; the FE never moves money here.
 */
import { useEffect, useState } from "react";
import { alpha, Btn, Card, Icon, Pill, T, tx } from "@v0y4ge/jrni-ui";
import { PageHeader } from "../../shell/chrome";
import { EmptyState } from "../../shell/composites";
import SettingsShell from "./SettingsShell";
import { DataSourceStrip } from "../../shell/opSource";
import { useT } from "../../lib/i18n";
import { getPayoutsOverview, formatMinor, type PayoutsOverview, type PayoutStatus, type Provider } from "./billing-api";

const BILLING_OPS = ["getPayoutsOverview"];

const payoutTone = (s: PayoutStatus): "success" | "warn" | "neutral" =>
  s === "paid" ? "success" : s === "in_transit" ? "warn" : "neutral";

const PROVIDER_META: Record<Provider, { label: string; tone: "primary" | "neutral" | "success" }> = {
  stripe: { label: "Stripe", tone: "primary" },
  paypal: { label: "PayPal", tone: "neutral" },
  adyen: { label: "Adyen", tone: "success" },
};
const provLabel = (p: Provider | string) => PROVIDER_META[p as Provider]?.label ?? p;
const provTone = (p: Provider | string) => PROVIDER_META[p as Provider]?.tone ?? "neutral";
/** Numeric last-4 → masked; a label ("PayPal balance") → shown as-is. */
const bankDisplay = (b: string) => (/^\d+$/.test(b) ? `•••• ${b}` : b);

export default function SettingsBilling() {
  const t = useT();
  const [data, setData] = useState<PayoutsOverview | null>(null);
  const [err, setErr] = useState(false);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let live = true;
    setData(null); setErr(false);
    getPayoutsOverview().then((d) => { if (live) setData(d); }).catch(() => { if (live) setErr(true); });
    return () => { live = false; };
  }, [tick]);

  const cur = data?.currency ?? "USD";
  const netThisPeriod = data ? data.grossThisPeriodMinor - data.feesThisPeriodMinor : 0;

  const statusLabel = (s: PayoutStatus) => ({
    paid: t("billing.payoutStatus.paid", "Paid"),
    in_transit: t("billing.payoutStatus.inTransit", "In transit"),
    pending: t("billing.payoutStatus.pending", "Pending"),
  }[s]);

  const fmtDate = (iso: string) => { try { return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", year: "numeric" }).format(new Date(iso)); } catch { return iso.slice(0, 10); } };

  return (
    <SettingsShell activeTab="billing">
      <PageHeader
        title={t("billing.title", "Billing & payouts")}
        subtitle={t("billing.subtitle", "Your ticket revenue and how it's paid out to your bank.")}
        actions={data && data.dashboardUrl && (data.providers?.length ?? 0) <= 1 ? (
          <Btn variant="secondary" icon={<Icon.external size={14} />} onClick={() => window.open(data.dashboardUrl!, "_blank", "noopener")}>
            {t("billing.openProviderDashboard", "Open payouts dashboard")}
          </Btn>
        ) : null}
      />
      <DataSourceStrip screen="billing" ops={BILLING_OPS} />

      <div style={{ flex: 1, overflow: "auto", padding: `${T.space[4]} ${T.space[6]} ${T.space[6]}`, display: "flex", flexDirection: "column", gap: T.space[4] }}>
        {err ? (
          <EmptyState
            variant="card"
            tone="danger"
            icon={<Icon.warn size={20} />}
            title={t("billing.loadErrorTitle", "Couldn't load payouts")}
            body={t("billing.loadError", "Something went wrong loading your payout summary. Please try again.")}
            action={<Btn variant="secondary" size="sm" icon={<Icon.refresh size={13} />} onClick={() => setTick((n) => n + 1)}>{t("common.actions.retry", "Try again")}</Btn>}
          />
        ) : !data ? (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: T.space[3] }}>
            {[0, 1, 2].map((i) => <Card key={i} style={{ height: 96, opacity: 0.55 }} />)}
          </div>
        ) : (
          <>
            {/* Balance / next payout */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: T.space[3] }}>
              <BalanceTile label={t("billing.available", "Available balance")} value={formatMinor(data.availableMinor, cur)} accent={T.green1} icon={<Icon.check size={15} />} />
              <BalanceTile label={t("billing.inTransit", "In transit")} value={formatMinor(data.pendingMinor, cur)} accent={T.orange1} icon={<Icon.refresh size={15} />} />
              <BalanceTile
                label={t("billing.nextPayout", "Next payout")}
                value={data.nextPayout ? formatMinor(data.nextPayout.amountMinor, cur) : "—"}
                sub={data.nextPayout ? fmtDate(data.nextPayout.date) : t("billing.noScheduledPayout", "None scheduled")}
                accent={T.p1} icon={<Icon.calendar size={15} />}
              />
            </div>

            {/* How payouts work */}
            {/* ui-raw: tonal callout is bespoke (T.p4 solid tint, 14/16 padding, T.n2/1.55 body text) —
                jrni-ui Banner's tone="primary" renders alpha(accent,9) tint, 12/14 padding, and a
                fixed T.n1/1.5 body color with no override slot for the paragraph, so adopting it
                would shift the tint/spacing/text color. Left raw per parity. */}
            <Card style={{ padding: `${T.space[4]} ${T.space[4]}`, background: T.p4, borderColor: alpha(T.p1, "22"), display: "flex", gap: T.space[2], alignItems: "flex-start" }}>
              <span style={{ color: T.p1, flexShrink: 0, marginTop: T.space[1] }}><Icon.info size={15} /></span>
              <div style={{ fontSize: T.text.bodySm.fontSize, color: T.n2, lineHeight: 1.55 }}>
                {t("billing.howItWorks", "Ticket revenue is collected into your connected")}{" "}
                <b style={{ color: T.n1 }}>{data.providers?.length ? data.providers.map((p) => provLabel(p.provider)).join(" · ") : provLabel(data.provider ?? t("billing.provider", "payment"))}</b>{" "}
                {data.providers && data.providers.length > 1
                  ? t("billing.howItWorksMulti", "accounts and paid to your bank on each provider's own rolling schedule. Manage schedules and bank details in each provider's dashboard below.")
                  : t("billing.howItWorks2", "account and paid to your bank on a rolling schedule. Manage the payout schedule and bank details in your provider dashboard.")}
              </div>
            </Card>

            {/* Payout accounts — per-provider breakdown (a tenant may have more than one live) */}
            {data.providers && data.providers.length > 0 ? (
              <Card style={{ padding: 0 }}>
                <div style={{ padding: `${T.space[3]} ${T.space[4]}`, borderBottom: `1px solid ${T.border}` }}>
                  <h3 style={{ ...tx.h3, fontWeight: 600, margin: 0 }}>{t("billing.payoutAccounts", "Payout accounts")}</h3>
                  <p style={{ ...tx.help, marginTop: T.space[1] }}>{t("billing.payoutAccountsHelp", "Where your ticket revenue is collected and paid out. Each connected provider settles on its own schedule and fee.")}</p>
                </div>
                <div style={{ overflowX: "auto" }}>
                  {/* ui-raw: hand-rolled <table> per house convention (T-token tables) — jrni-ui Table's
                      fixed header treatment (h3-weight label, var(--jrni-color-neutral-5) border) can't
                      reproduce this screen's 11px uppercase letter-spaced header / T.border rule without
                      a visible redesign, so parity would break. */}
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: T.text.body.fontSize, minWidth: 640 }}>
                    <thead>
                      <tr>
                        {[t("billing.col.provider", "Provider"), t("billing.available", "Available"), t("billing.inTransit", "In transit"), t("billing.col.fee", "Fee"), t("billing.grossSales", "Gross (period)"), ""].map((h, i) => (
                          <th key={i} style={{ textAlign: i >= 1 && i <= 4 ? "right" : "left", padding: `${T.space[3]} ${T.space[4]}`, background: T.n6, borderBottom: `1px solid ${T.border}`, fontSize: T.text.label.fontSize, textTransform: "uppercase", letterSpacing: 0.04, color: T.soft, fontWeight: 700, whiteSpace: "nowrap" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {data.providers.map((p) => (
                        <tr key={p.provider} style={{ borderBottom: `1px solid ${T.border}` }}>
                          <td style={{ padding: `${T.space[3]} ${T.space[4]}` }}>
                            <Pill tone={provTone(p.provider)} dot>{provLabel(p.provider)}</Pill>
                            <div style={{ fontSize: T.text.label.fontSize, color: T.soft, marginTop: T.space[1], fontFamily: T.fontMono }}>{bankDisplay(p.bankLast4)}</div>
                          </td>
                          <td style={{ padding: `${T.space[3]} ${T.space[4]}`, textAlign: "right", fontWeight: 700, color: T.n1, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>{formatMinor(p.availableMinor, cur)}</td>
                          <td style={{ padding: `${T.space[3]} ${T.space[4]}`, textAlign: "right", color: T.orange1, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>{formatMinor(p.pendingMinor, cur)}</td>
                          <td style={{ padding: `${T.space[3]} ${T.space[4]}`, textAlign: "right", color: T.n2, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>{(p.feeBps / 100).toFixed(2)}%</td>
                          <td style={{ padding: `${T.space[3]} ${T.space[4]}`, textAlign: "right", color: T.n1, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>{formatMinor(p.grossThisPeriodMinor, cur)}</td>
                          <td style={{ padding: `${T.space[3]} ${T.space[4]}`, textAlign: "right", whiteSpace: "nowrap" }}>
                            {p.dashboardUrl ? (
                              <Btn variant="ghost" size="sm" icon={<Icon.external size={12} />} onClick={() => window.open(p.dashboardUrl!, "_blank", "noopener")}>{t("billing.dashboard", "Dashboard")}</Btn>
                            ) : null}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            ) : null}

            {/* Fees this period */}
            <Card style={{ padding: 0 }}>
              <div style={{ padding: `${T.space[3]} ${T.space[4]}`, borderBottom: `1px solid ${T.border}` }}>
                <h3 style={{ ...tx.h3, fontWeight: 600, margin: 0 }}>{t("billing.thisPeriod", "This period")}</h3>
              </div>
              <div style={{ padding: `${T.space[4]} ${T.space[4]}`, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: T.space[3] }}>
                <MiniKV k={t("billing.grossSales", "Gross sales")} v={formatMinor(data.grossThisPeriodMinor, cur)} />
                <MiniKV k={t("billing.platformFee", "Platform fee")} v={`${(data.feeBps / 100).toFixed(2)}% · ${formatMinor(data.feesThisPeriodMinor, cur)}`} tone="warn" />
                <MiniKV k={t("billing.netToYou", "Net to you")} v={formatMinor(netThisPeriod, cur)} tone="success" />
              </div>
            </Card>

            {/* Payout history */}
            <Card style={{ padding: 0 }}>
              <div style={{ padding: `${T.space[3]} ${T.space[4]}`, borderBottom: `1px solid ${T.border}` }}>
                <h3 style={{ ...tx.h3, fontWeight: 600, margin: 0 }}>{t("billing.recentPayouts", "Recent payouts")}</h3>
              </div>
              {data.payouts.length === 0 ? (
                <EmptyState
                  variant="plain"
                  compact
                  icon={<Icon.calendar size={18} />}
                  title={t("billing.noPayoutsTitle", "No payouts yet")}
                  body={t("billing.noPayouts", "Your first payout lands after your first sales settle.")}
                />
              ) : (
                <div style={{ overflowX: "auto" }}>
                  {/* ui-raw: hand-rolled <table> per house convention (T-token tables) — same reasoning
                      as the payout-accounts table above (jrni-ui Table's fixed header typography/border
                      token would visibly redesign this header row). */}
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: T.text.body.fontSize, minWidth: 560 }}>
                    <thead>
                      <tr>
                        {[t("billing.col.initiated", "Initiated"), t("billing.col.provider", "Provider"), t("billing.col.amount", "Amount"), t("billing.col.status", "Status"), t("billing.col.arrival", "Arrival"), t("billing.col.bank", "Bank")].map((h, i) => (
                          <th key={i} style={{ textAlign: i === 2 ? "right" : "left", padding: `${T.space[3]} ${T.space[4]}`, background: T.n6, borderBottom: `1px solid ${T.border}`, fontSize: T.text.label.fontSize, textTransform: "uppercase", letterSpacing: 0.04, color: T.soft, fontWeight: 700, whiteSpace: "nowrap" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {data.payouts.map((p) => (
                        <tr key={p.id} style={{ borderBottom: `1px solid ${T.border}` }}>
                          <td style={{ padding: `${T.space[3]} ${T.space[4]}`, color: T.n1, whiteSpace: "nowrap" }}>{fmtDate(p.createdAt)}</td>
                          <td style={{ padding: `${T.space[3]} ${T.space[4]}`, whiteSpace: "nowrap" }}>{p.provider ? <Pill tone={provTone(p.provider)}>{provLabel(p.provider)}</Pill> : <span style={{ color: T.soft }}>—</span>}</td>
                          <td style={{ padding: `${T.space[3]} ${T.space[4]}`, textAlign: "right", fontWeight: 700, color: T.n1, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>{formatMinor(p.amountMinor, p.currency)}</td>
                          <td style={{ padding: `${T.space[3]} ${T.space[4]}` }}><Pill tone={payoutTone(p.status)}>{statusLabel(p.status)}</Pill></td>
                          <td style={{ padding: `${T.space[3]} ${T.space[4]}`, color: T.soft, whiteSpace: "nowrap" }}>{fmtDate(p.arrivalDate)}</td>
                          <td style={{ padding: `${T.space[3]} ${T.space[4]}`, color: T.soft, fontFamily: T.fontMono, fontSize: T.text.label.fontSize }}>{bankDisplay(p.bankLast4)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </>
        )}
      </div>
    </SettingsShell>
  );
}

// ui-raw: KPI tile is bespoke (icon+label grouped left, 24px/800 value, optional sub-label
// below value) — jrni-ui's StatTile puts the icon top-right, sizes the value 28px/700, and has
// no sub-label slot, so swapping would visibly redesign this tile rather than mechanically
// swap the primitive. Left raw per parity.
function BalanceTile({ label, value, sub, accent, icon }: { label: string; value: string; sub?: string; accent: string; icon: React.ReactNode }) {
  return (
    <Card style={{ padding: `${T.space[4]} ${T.space[4]}`, display: "flex", flexDirection: "column", gap: T.space[2] }}>
      <div style={{ display: "flex", alignItems: "center", gap: T.space[2] }}>
        <span style={{ width: 26, height: 26, borderRadius: T.radius.md, background: alpha(accent, "16"), color: accent, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>{icon}</span>
        <span style={{ fontSize: T.text.label.fontSize, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.05, color: T.soft }}>{label}</span>
      </div>
      <div style={{ fontSize: T.text.display.fontSize, fontWeight: 800, color: T.n1, fontVariantNumeric: "tabular-nums" }}>{value}</div>
      {sub ? <div style={{ fontSize: T.text.bodySm.fontSize, color: T.soft }}>{sub}</div> : null}
    </Card>
  );
}

function MiniKV({ k, v, tone }: { k: string; v: string; tone?: "success" | "warn" }) {
  const color = tone === "success" ? T.green1 : tone === "warn" ? T.orange1 : T.n1;
  return (
    <div>
      <div style={{ fontSize: T.text.label.fontSize, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.04, color: T.soft }}>{k}</div>
      <div style={{ fontSize: T.text.h3.fontSize, fontWeight: 700, color, marginTop: T.space[1], fontVariantNumeric: "tabular-nums" }}>{v}</div>
    </div>
  );
}
